import { MainRouteUrls } from '@domain/types';
import { APP_URLS, BASENAME, LAST_APP_VIEWED_LOCALSTORAGE_KEY } from '@launcher/constants';
import { useFeatureFlags } from '@launcher/context/FeatureFlags.context';
import { Breakpoint, useViewportLargerThan } from '@launcher/context/Viewport.context';
import { getCurrentAppFromPathname } from '@launcher/utils';
import { getIsLifenSendingActivated, selectActiveApps } from '@store/user/user.selector';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsAppLoading } from './useIsAppLoading.hook';

// This would probably better be done in a saga, keeping it here to not break current workflow
// Tech backlog card: https://honestica.atlassian.net/browse/CORE-3105
export function useDefaultAppRoute() {
  const coreApps = useSelector(selectActiveApps);
  const isLoading = useIsAppLoading();
  const isDocumentsAppAvailable = useSelector(getIsLifenSendingActivated);
  const featureFlags = useFeatureFlags();

  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showOutgoing = useViewportLargerThan(Breakpoint.L);
  const currentAppName = useMemo(() => {
    if (isLoading) return null;
    return getCurrentAppFromPathname(pathname, coreApps);
  }, [isLoading, coreApps, pathname]);

  const isDesktopSettings = useMemo(
    () => pathname === `${BASENAME}/${MainRouteUrls.DesktopSettings}`,
    [pathname],
  );
  const lastApp = localStorage.getItem(LAST_APP_VIEWED_LOCALSTORAGE_KEY);

  useEffect(() => {
    if (featureFlags['cafe-maintenance']) {
      navigate(MainRouteUrls.Maintenance);
      return;
    }
    // If the app is loading, or if we are already in a specific app or desktop settings, do not redirect
    if (isLoading || currentAppName !== null || isDesktopSettings) {
      return;
    }

    // Try to navigate to last viewed app if it's still available
    if (lastApp && lastApp in APP_URLS && coreApps.some((app) => app.name === lastApp)) {
      navigate(APP_URLS[lastApp as keyof typeof APP_URLS]);
      return;
    }

    // Fallback to Documents app if available
    if (isDocumentsAppAvailable) {
      navigate(MainRouteUrls.Documents);
      return;
    }

    // Default to first available app
    if (coreApps.length > 0) {
      const defaultAppKey = coreApps[0].name;
      navigate(APP_URLS[defaultAppKey]);
    }
  }, [
    dispatch,
    coreApps,
    isLoading,
    currentAppName,
    lastApp,
    isDocumentsAppAvailable,
    isDesktopSettings,
    navigate,
    showOutgoing,
    featureFlags,
  ]);
}
